import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import styles from './ChangePassword.module.css';
import { Button, ButtonType, ButtonVariant, LabeledFormField, Notify } from 'components/core';
import { IconButton } from '../../core/buttons/IconButton';
import { RouteConstants } from '../../../constants';
import { usePasswordService, useUserService } from 'services';
import { authState, User } from 'states';
import { ApiError, isPasswordChangeRequired } from 'helpers';

interface IFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordHeaderProps {
  onBack: () => void;
  isForced: boolean;
}

const ChangePasswordHeader = ({ onBack, isForced }: ChangePasswordHeaderProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.pageHeader}>
      {!isForced && (
        <button type="button" className={styles.backBtn} onClick={onBack} title={t('ACTION.BACK')}>
          <span className="material-icons" aria-hidden="true">arrow_back</span>
        </button>
      )}
      <div>
        <h1 className={styles.pageTitle}>{t('AUTH.CHANGE_PASSWORD_TITLE')}</h1>
        <div className={styles.pageSubtitle}>{t('AUTH.CHANGE_PASSWORD_SUBTITLE')}</div>
      </div>
    </div>
  );
};

// Shown when a new user is still on the default password emailed at onboarding.
const ForcedChangeNotice = () => {
  const { t } = useTranslation();
  return (
    <div className="alert alert-info d-flex align-items-start gap-2" role="alert">
      <span className="material-icons" aria-hidden="true">info</span>
      <span>{t('AUTH.FORCED_PASSWORD_CHANGE_MESSAGE')}</span>
    </div>
  );
};

interface ChangePasswordFormProps {
  onSubmit: (values: IFormValues) => Promise<void>;
  onCancel: () => void;
  onLogout: () => void;
  isForced: boolean;
}

// Built from `t` inside the component (memoized on `t`) rather than as a
// module-level constant, which would freeze error messages in whatever
// language was active at load time.
const createChangePasswordValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    currentPassword: Yup.string().required(t('AUTH.CURRENT_PASSWORD_REQUIRED')),
    newPassword: Yup.string()
      .required(t('AUTH.NEW_PASSWORD_REQUIRED'))
      .min(8, t('AUTH.PASSWORD_MIN_LENGTH'))
      .notOneOf([Yup.ref('currentPassword')], t('AUTH.PASSWORD_SAME_AS_CURRENT')),
    confirmPassword: Yup.string()
      .required(t('AUTH.CONFIRM_PASSWORD_REQUIRED'))
      .oneOf([Yup.ref('newPassword')], t('AUTH.PASSWORD_MISMATCH')),
  });

const ChangePasswordForm = ({ onSubmit, onCancel, onLogout, isForced }: ChangePasswordFormProps) => {
  const { t } = useTranslation();
  const changePasswordValidationSchema = useMemo(() => createChangePasswordValidationSchema(t), [t]);
  const { register, handleSubmit, formState } = useForm<IFormValues>({
    resolver: yupResolver(changePasswordValidationSchema),
  });
  const { errors, isSubmitting } = formState;

  return (
    <div className={styles.card}>
      {isForced && <ForcedChangeNotice />}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LabeledFormField
          className={styles.fieldGroup}
          label={t('AUTH.CURRENT_PASSWORD_LABEL')}
          icon="lock"
          type="password"
          placeholder={t('AUTH.CURRENT_PASSWORD_PLACEHOLDER')}
          required
          autoFocus
          error={errors.currentPassword?.message}
          registration={register('currentPassword')}
        />

        <LabeledFormField
          className={styles.fieldGroup}
          label={t('AUTH.CHANGE_NEW_PASSWORD_LABEL')}
          icon="lock"
          type="password"
          placeholder={t('AUTH.CHANGE_NEW_PASSWORD_PLACEHOLDER')}
          required
          error={errors.newPassword?.message}
          registration={register('newPassword')}
        />

        <LabeledFormField
          className={styles.fieldGroup}
          label={t('AUTH.CHANGE_CONFIRM_PASSWORD_LABEL')}
          icon="lock"
          type="password"
          placeholder={t('AUTH.CHANGE_CONFIRM_PASSWORD_PLACEHOLDER')}
          required
          error={errors.confirmPassword?.message}
          registration={register('confirmPassword')}
        />

        <div className={styles.footer}>
          {/* A forced change can't be skipped - the only way out is to log out. */}
          <Button type={ButtonType.secondary} variant={ButtonVariant.outline} onClick={isForced ? onLogout : onCancel} disabled={isSubmitting}>
            {isForced ? t('AUTH.LOGOUT') : t('ACTION.CANCEL')}
          </Button>
          <IconButton
            icon="check"
            label={isSubmitting ? t('AUTH.CHANGING_PASSWORD') : t('AUTH.CHANGE_PASSWORD_BUTTON')}
            onClick={handleSubmit(onSubmit)}
            type={ButtonType.primary}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );
};

export const ChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useRecoilValue(authState) as User;
  const passwordService = usePasswordService();
  const userService = useUserService();
  // Forced when redirected here after login / by the route guard, or whenever
  // the session still says the default password hasn't been changed.
  const isForced = (location.state as { forced?: boolean } | null)?.forced === true || isPasswordChangeRequired(auth);

  const goBack = useCallback(() => navigate(RouteConstants.dashboards), [navigate]);

  const onSubmit = useCallback(
    (values: IFormValues) => {
      return passwordService
        .changePassword(auth?.id ?? null, values.currentPassword, values.newPassword, values.confirmPassword)
        .then(() => {
          // The current token was issued for the temporary password and the
          // backend keeps rejecting it (PASSWORD_RESET_REQUIRED) even after the
          // change, so a fresh login is the only way to get a usable token.
          if (isForced) {
            Notify.success(t('AUTH.FORCED_PASSWORD_CHANGED_LOGIN_AGAIN'));
            userService.logout();
            return;
          }
          userService.markPasswordUpdated(auth);
          Notify.success(t('AUTH.CHANGE_PASSWORD_SUCCESS_MESSAGE'));
          navigate(RouteConstants.dashboards, { replace: true });
        })
        .catch((message: ApiError) => Notify.error(message || t('AUTH.CHANGE_PASSWORD_FAILED_FALLBACK')));
    },
    [auth, isForced, navigate, passwordService, userService, t],
  );

  return (
    <div className={styles.page}>
      <ChangePasswordHeader onBack={goBack} isForced={isForced} />
      <ChangePasswordForm onSubmit={onSubmit} onCancel={goBack} onLogout={userService.logout} isForced={isForced} />
    </div>
  );
};
